import { useState, useMemo, useRef, useEffect } from 'react';
import { DailyTransactionStatsDTO } from '@/services/api.service';
import styles from './StackedBarChart.module.scss';

// Generate consistent colors for dynamic reasons
const COLORS = [
  '#F59E0B', // Yellow/Gold
  '#06B6D4', // Cyan
  '#EC4899', // Pink
  '#F97316', // Orange
  '#8B5CF6', // Purple
  '#10B981', // Green
  '#EF4444', // Red
  '#3B82F6', // Blue
  '#6366F1', // Indigo
  '#14B8A6', // Teal
  '#F472B6', // Pink light
  '#A855F7', // Purple light
];

function getColorForReason(_reason: string, index: number): string {
  return COLORS[index % COLORS.length] || '#6B7280';
}

interface StackedBarChartProps {
  data: DailyTransactionStatsDTO[];
  isNegative?: boolean; // For "clear" transactions, show bars going down
  barWidthRatio?: number; // Part de l'espace occupée par la barre elle-même (0-1), le reste = espace entre barres
  rawLabels?: boolean; // Si true, `data[].date` est affiché tel quel (ex: "Mineur") au lieu d'être interprété comme une date
  unit?: string; // Suffixe affiché après chaque valeur (ex: "$" pour de l'argent, "" pour un simple compte)
  showSign?: boolean; // Si true, préfixe les valeurs de +/- (pertinent pour de l'argent, pas pour un compte)
}

export function StackedBarChart({
  data,
  isNegative = false,
  barWidthRatio = 0.6,
  rawLabels = false,
  unit = '$',
  showSign = true,
}: StackedBarChartProps) {
  const [hoveredBar, setHoveredBar] = useState<{ index: number; x: number; y: number } | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartSize, setChartSize] = useState({ width: 800, height: 300 });

  // Measure chart size
  useEffect(() => {
    const updateSize = () => {
      if (chartRef.current) {
        const rect = chartRef.current.getBoundingClientRect();
        setChartSize({ width: rect.width, height: rect.height });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Get all unique reasons and assign colors
  const reasonColors = useMemo(() => {
    const reasons = new Set<string>();
    data.forEach(d => {
      Object.keys(d.reasons || {}).forEach(r => reasons.add(r));
    });

    const colors: Record<string, string> = {};
    const sortedReasons = Array.from(reasons).sort();
    sortedReasons.forEach((reason, index) => {
      colors[reason] = getColorForReason(reason, index);
    });
    return colors;
  }, [data]);

  // Get active reasons (ones that have data) sorted by total amount
  const activeReasons = useMemo(() => {
    const totals: Record<string, number> = {};
    data.forEach(d => {
      Object.entries(d.reasons || {}).forEach(([reason, val]) => {
        totals[reason] = (totals[reason] || 0) + val;
      });
    });

    return Object.entries(totals)
      .sort((a, b) => b[1] - a[1])
      .map(([reason]) => reason);
  }, [data]);

  // Calculate chart data
  const chartData = useMemo(() => {
    if (data.length === 0) return { bars: [], maxY: 0, yLabels: [] };

    // Find max total for Y axis
    const maxTotal = Math.max(...data.map(d => d.total), 1);

    // Add padding to max
    const paddedMax = maxTotal * 1.1;

    // Generate Y axis labels (5 labels)
    const yLabels: number[] = [];
    for (let i = 0; i <= 4; i++) {
      yLabels.push((paddedMax / 4) * i);
    }

    // Calculate bar data
    const barWidth = (chartSize.width / data.length) * barWidthRatio;
    const barGap = (chartSize.width / data.length) * (1 - barWidthRatio);

    const bars = data.map((d, i) => {
      const x = (i * (barWidth + barGap)) + (barGap / 2);
      const segments: { reason: string; height: number; y: number; value: number; color: string }[] = [];

      let currentY = isNegative ? 0 : chartSize.height;

      // Build segments for each reason (in order of total importance)
      activeReasons.forEach(reason => {
        const value = (d.reasons || {})[reason] || 0;
        if (value > 0) {
          const height = (value / paddedMax) * chartSize.height;

          if (isNegative) {
            segments.push({
              reason,
              height,
              y: currentY,
              value,
              color: reasonColors[reason] || '#6B7280',
            });
            currentY += height;
          } else {
            currentY -= height;
            segments.push({
              reason,
              height,
              y: currentY,
              value,
              color: reasonColors[reason] || '#6B7280',
            });
          }
        }
      });

      return {
        x,
        width: barWidth,
        segments,
        data: d,
      };
    });

    return { bars, maxY: paddedMax, yLabels: yLabels.reverse() };
  }, [data, chartSize, isNegative, activeReasons, reasonColors, barWidthRatio]);

  const formatMoney = (amount: number): string => {
    if (amount >= 1_000_000_000) {
      return `${(amount / 1_000_000_000).toFixed(1)}B`;
    }
    if (amount >= 1_000_000) {
      return `${(amount / 1_000_000).toFixed(1)}M`;
    }
    if (amount >= 1_000) {
      return `${(amount / 1_000).toFixed(0)}K`;
    }
    return amount.toLocaleString('fr-FR');
  };

  const formatDate = (dateStr: string): string => {
    if (rawLabels) return dateStr;
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatShortDate = (dateStr: string): string => {
    if (rawLabels) return dateStr;
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>, barIndex: number) => {
    const rect = chartRef.current?.getBoundingClientRect();
    if (!rect) return;

    const bar = chartData.bars[barIndex];
    if (!bar) return;

    setHoveredBar({
      index: barIndex,
      x: bar.x + bar.width / 2,
      y: e.clientY - rect.top,
    });
  };

  if (data.length === 0) {
    return (
      <div className={styles.noData}>
        <span className={styles.noDataIcon}>📊</span>
        <p>Aucune donnee disponible</p>
      </div>
    );
  }

  return (
    <div className={styles.chartContainer}>
      {/* Legend */}
      <div className={styles.legend}>
        {activeReasons.slice(0, 8).map(reason => (
          <div key={reason} className={styles.legendItem}>
            <span
              className={styles.legendColor}
              style={{ backgroundColor: reasonColors[reason] }}
            />
            <span className={styles.legendLabel} title={reason}>
              {reason.length > 25 ? reason.substring(0, 25) + '...' : reason}
            </span>
          </div>
        ))}
        {activeReasons.length > 8 && (
          <div className={styles.legendItem}>
            <span className={styles.legendLabel}>+{activeReasons.length - 8} autres</span>
          </div>
        )}
      </div>

      {/* Chart */}
      <div className={styles.chart}>
        {/* Y Axis */}
        <div className={styles.yAxis}>
          {chartData.yLabels.map((label, i) => (
            <span key={i} className={styles.yLabel}>
              {isNegative ? `-${formatMoney(label)}` : formatMoney(label)}
            </span>
          ))}
        </div>

        {/* Chart Area */}
        <div
          ref={chartRef}
          className={styles.chartArea}
          onMouseLeave={() => setHoveredBar(null)}
        >
          <svg
            width={chartSize.width}
            height={chartSize.height}
            className={styles.chartSvg}
          >
            {/* Grid lines */}
            <g className={styles.gridLines}>
              {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
                <line
                  key={i}
                  x1="0"
                  y1={ratio * chartSize.height}
                  x2={chartSize.width}
                  y2={ratio * chartSize.height}
                />
              ))}
            </g>

            {/* Bars */}
            {chartData.bars.map((bar, barIndex) => (
              <g
                key={barIndex}
                onMouseMove={(e) => handleMouseMove(e as unknown as React.MouseEvent<HTMLDivElement>, barIndex)}
                style={{ cursor: 'pointer' }}
              >
                {/* Invisible hit area */}
                <rect
                  x={bar.x - 2}
                  y={0}
                  width={bar.width + 4}
                  height={chartSize.height}
                  fill="transparent"
                />

                {/* Stacked segments */}
                {bar.segments.map((segment, segIndex) => (
                  <rect
                    key={segIndex}
                    x={bar.x}
                    y={segment.y}
                    width={bar.width}
                    height={segment.height}
                    fill={segment.color}
                    rx={segIndex === (isNegative ? bar.segments.length - 1 : 0) ? 4 : 0}
                    ry={segIndex === (isNegative ? bar.segments.length - 1 : 0) ? 4 : 0}
                    opacity={hoveredBar?.index === barIndex ? 1 : 0.85}
                    style={{ transition: 'opacity 0.15s ease' }}
                  />
                ))}

                {/* Hover highlight */}
                {hoveredBar?.index === barIndex && (
                  <rect
                    x={bar.x - 2}
                    y={0}
                    width={bar.width + 4}
                    height={chartSize.height}
                    fill="rgba(255, 255, 255, 0.05)"
                    pointerEvents="none"
                  />
                )}
              </g>
            ))}
          </svg>

          {/* Tooltip */}
          {hoveredBar !== null && (() => {
            const bar = chartData.bars[hoveredBar.index];
            if (!bar) return null;
            return (
              <div
                className={`${styles.tooltip} ${hoveredBar.x > chartSize.width * 0.75 ? styles.tooltipLeft : ''}`}
                style={{
                  left: hoveredBar.x,
                  top: 10,
                }}
              >
                <span className={styles.tooltipDate}>
                  {formatDate(bar.data.date)}
                </span>
                <div className={styles.tooltipValues}>
                  {bar.segments.map((seg, i) => (
                    <div key={i} className={styles.tooltipRow}>
                      <span
                        className={styles.tooltipColor}
                        style={{ backgroundColor: seg.color }}
                      />
                      <span className={styles.tooltipReason} title={seg.reason}>
                        {seg.reason.length > 30 ? seg.reason.substring(0, 30) + '...' : seg.reason}
                      </span>
                      <span className={styles.tooltipAmount}>
                        {showSign ? (isNegative ? '-' : '+') : ''}{formatMoney(seg.value)}{unit && ` ${unit}`}
                      </span>
                    </div>
                  ))}
                </div>
                <div className={styles.tooltipTotal}>
                  Total: {showSign ? (isNegative ? '-' : '+') : ''}{formatMoney(bar.data.total)}{unit && ` ${unit}`}
                </div>
              </div>
            );
          })()}
        </div>

        {/* X Axis */}
        <div className={styles.xAxis}>
          {data.length <= 10 ? (
            data.map((d, i) => {
              const bar = chartData.bars[i];
              if (!bar) return null;
              return (
                <span
                  key={i}
                  className={styles.xLabel}
                  style={{ left: bar.x + bar.width / 2 }}
                >
                  {formatShortDate(d.date)}
                </span>
              );
            })
          ) : (
            Array.from({ length: 7 }, (_, i) => {
              const index = Math.round((i / 6) * (data.length - 1));
              const bar = chartData.bars[index];
              const dataPoint = data[index];
              if (!bar || !dataPoint) return null;
              const leftPos = bar.x + bar.width / 2;
              return (
                <span
                  key={i}
                  className={styles.xLabel}
                  style={{ left: leftPos }}
                >
                  {formatShortDate(dataPoint.date)}
                </span>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
